import {
  Api,
  ApiGetDescriptionDataRequestFromJSON,
  ApiWorkflowStepRequest,
  ApiWorkflowStepResponse,
  ApiGetGeoUnitsRequestFromJSON,
  ApiSearchRentalCarStationsRequestFromJSON,
  ApiItemType,
  ApiConfigurationDataRequestFromJSON,
  ApiConfigurationDataType,
  ApiGetGeoUnitsResponse,
  ApiDescriptionData,
  ApiSearchRentalCarStationsResponse,
  ApiConfigurationDataResponse,
  ApiBaseData,
  ApiConfigurationDataFilterFromJSON
} from '@ibe/api';

import LoggerFactory from '../../Util/Logger/LoggerFactory';

class ApiService extends Api {
  private logger = LoggerFactory.get('ApiService');

  public async getTermsAndConditionsForService(
    type: string,
    serviceCode: string
  ): Promise<Array<ApiDescriptionData>> {
    const request = ApiGetDescriptionDataRequestFromJSON({
      code: serviceCode,
      type,
      descriptionCategoryType: 'TC'
    });
    return this.getServiceDescriptionData(request);
  }

  public async getTermsAndConditionsForUnit(
    serviceType: string,
    serviceCode: string,
    unitType: string,
    unitCode: string
  ): Promise<Array<ApiDescriptionData>> {
    const request = ApiGetDescriptionDataRequestFromJSON({
      code: unitCode,
      type: unitType,
      parentCode: serviceCode,
      parentType: serviceType,
      descriptionCategoryType: 'TC'
    });
    return this.getServiceUnitDescriptionData(request);
  }

  public async getGeoUnitsForItemType(itemType: ApiItemType): Promise<ApiGetGeoUnitsResponse> {
    const request = ApiGetGeoUnitsRequestFromJSON({
      type: 'GetGeoUnitsRequest',
      itemType
    });
    return this.getGeoUnits(request);
  }

  public async getPackageServiceGeoUnits(): Promise<ApiGetGeoUnitsResponse> {
    const request = ApiGetGeoUnitsRequestFromJSON({
      type: 'GetGeoUnitsRequest',
      itemType: ApiItemType.PACKAGE
    });
    return this.getGeoUnits(request);
  }

  public async getRentalCarGeoUnits(): Promise<ApiGetGeoUnitsResponse> {
    const request = ApiGetGeoUnitsRequestFromJSON({
      type: 'GetGeoUnitsRequest',
      itemType: ApiItemType.CAR
    });
    return this.getGeoUnits(request);
  }

  public loadStep(
    apiWorkflowStepRequest?: ApiWorkflowStepRequest
  ): Promise<ApiWorkflowStepResponse> {
    this.logger.warn('ATTENTION: YOU ARE USING A MOCKED API!');
    return super.loadStep(apiWorkflowStepRequest);
  }

  public async searchRentalCarStationsService(
    value: string
  ): Promise<ApiSearchRentalCarStationsResponse> {
    const request = ApiSearchRentalCarStationsRequestFromJSON({
      searchExpression: value
    });
    return this.searchRentalCarStations(request);
  }

  public async getCountriesWithChildren(): Promise<ApiGetGeoUnitsResponse> {
    return this.getCountries();
  }

  public async getSalutations(): Promise<ApiConfigurationDataResponse> {
    const request = ApiConfigurationDataRequestFromJSON({
      typeCodes: [ApiConfigurationDataType.SALUTATIONS]
    });
    return this.getConfiguration(request);
  }

  public async getAllCountries(languageCode?: string): Promise<Array<ApiBaseData>> {
    let request = ApiConfigurationDataRequestFromJSON({
      typeCodes: [ApiConfigurationDataType.COUNTRIES]
    });
    if (!!languageCode) {
      request = { ...request, languageCode };
    }
    const res = await this.getConfiguration(request);
    return res.data[ApiConfigurationDataType.COUNTRIES].sort((c1, c2) =>
      c1.description.localeCompare(c2.description)
    );
  }

  public async getCitiesAndCountriesFromPostalCode(
    postalCodes: string[],
    countryCodes?: string[],
    languageCode?: string
  ): Promise<Array<ApiBaseData>> {
    const request = ApiConfigurationDataRequestFromJSON({
      typeCodes: [ApiConfigurationDataType.POSTALCODE],
      languageCode,
      elementFilters: [
        ApiConfigurationDataFilterFromJSON({
          element: ApiConfigurationDataType.POSTALCODE,
          codes: postalCodes
        }),
        ...(!!countryCodes
          ? [
              ApiConfigurationDataFilterFromJSON({
                element: ApiConfigurationDataType.COUNTRIES,
                codes: countryCodes
              })
            ]
          : [])
      ]
    });
    return (await this.getConfiguration(request))?.data?.[ApiConfigurationDataType.POSTALCODE];
  }

  public async getNationalities(): Promise<Array<ApiBaseData>> {
    const request = ApiConfigurationDataRequestFromJSON({
      typeCodes: [ApiConfigurationDataType.NATIONALITIES]
    });
    const res = await this.getConfiguration(request);
    return res.data[ApiConfigurationDataType.NATIONALITIES].sort((c1, c2) =>
      c1.description.localeCompare(c2.description)
    );
  }

  public async getCountryRegions(): Promise<Map<ApiBaseData, ApiBaseData[]>> {
    const request = ApiConfigurationDataRequestFromJSON({
      typeCodes: [ApiConfigurationDataType.COUNTRYREGIONS, ApiConfigurationDataType.COUNTRIES]
    });
    const res = await this.getConfiguration(request);
    const resultMap = new Map<ApiBaseData, ApiBaseData[]>();

    const countries = res.data[ApiConfigurationDataType.COUNTRIES];
    const countryRegions =
      res.data[ApiConfigurationDataType.COUNTRYREGIONS] || new Array<ApiBaseData>();

    countries.forEach(value => {
      resultMap.set(value, []);
    });

    const findKey = (code: string): ApiBaseData | undefined => {
      return countries.find(v => v.code === code);
    };

    countryRegions.forEach(value => {
      if (value.group) {
        const key = findKey(value.group);
        if (key) {
          resultMap.get(key)?.push(value);
        }
      }
    });
    return resultMap;
  }

  public async getAllLanguages(): Promise<Array<ApiBaseData>> {
    const request = ApiConfigurationDataRequestFromJSON({
      typeCodes: [ApiConfigurationDataType.LANGUAGES]
    });
    const res = await this.getConfiguration(request);
    return res.data[ApiConfigurationDataType.LANGUAGES];
  }

  public async getAllCurrencies(): Promise<Array<ApiBaseData>> {
    const request = ApiConfigurationDataRequestFromJSON({
      typeCodes: [ApiConfigurationDataType.CURRENCIES]
    });
    const res = await this.getConfiguration(request);
    return res.data[ApiConfigurationDataType.CURRENCIES];
  }

  public async getServiceUnitTypeCodes(itemType: ApiItemType): Promise<Array<ApiBaseData>> {
    const request = ApiConfigurationDataRequestFromJSON({
      typeCodes: [ApiConfigurationDataType.UNITTYPE],
      itemType
    });
    const res = await this.getConfiguration(request);
    return res.data[ApiConfigurationDataType.UNITTYPE];
  }

  public async getServiceGroups(itemType: ApiItemType): Promise<Array<ApiBaseData>> {
    const request = ApiConfigurationDataRequestFromJSON({
      typeCodes: [ApiConfigurationDataType.SERVICEGROUP],
      itemType
    });
    const res = await this.getConfiguration(request);
    return res.data[ApiConfigurationDataType.SERVICEGROUP];
  }

  public async getCabinClasses(): Promise<Array<ApiBaseData>> {
    const request = ApiConfigurationDataRequestFromJSON({
      typeCodes: [ApiConfigurationDataType.CABINCLASS]
    });
    const res = await this.getConfiguration(request);
    return res.data[ApiConfigurationDataType.CABINCLASS];
  }
}

export default ApiService;
